
import { useState, useEffect } from 'react';
import { X, Wrench, Loader2, Plus, XCircle } from 'lucide-react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VisibilitySelect, type CapabilityVisibility } from '@/components/ui/visibility-select';
import { logger } from '@/lib/telemetry';
import { capabilityColors } from '@/lib/capability-colors';
import { toast } from 'sonner';

interface CreatedTool {
    id: string;
    name: string;
    description?: string;
    handler_class?: string;
    tags?: string[];
}

interface ToolFormDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    /** App context for app-scoped tools */
    appSlug?: string;
    /** Callback when tool is successfully created (stays on current page) */
    onSuccess?: (tool: CreatedTool) => void;
}

export default function ToolFormDrawer({
    isOpen,
    onClose,
    appSlug,
    onSuccess,
}: ToolFormDrawerProps) {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    
    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [handlerClass, setHandlerClass] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState('');
    const [visibility, setVisibility] = useState<CapabilityVisibility>('account');

    // Reset form when opened
    useEffect(() => {
        if (isOpen) {
            setErrors({});
            setName('');
            setDescription('');
            setHandlerClass('');
            setTags([]);
            setTagInput('');
            setVisibility('account');
        }
    }, [isOpen]);

    const handleAddTag = () => {
        const tag = tagInput.trim();
        if (tag && !tags.includes(tag)) {
            setTags([...tags, tag]);
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setTags(tags.filter(t => t !== tagToRemove));
    };

    const handleTagKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const colors = capabilityColors.tool;

    const handleSubmit = async () => {
        if (!name.trim()) {
            setErrors({ name: 'Name is required' });
            return;
        }
        
        setLoading(true);
        setErrors({});
        
        const formData = {
            name: name.trim(),
            description: description.trim() || null,
            handler_class: handlerClass.trim() || null,
            tags: tags.length > 0 ? tags : null,
            visibility,
        };

        const baseUrl = appSlug ? `/admin/apps/${appSlug}/tools` : '/admin/tools';

        try {
            // Use axios to avoid Inertia navigation - we want to stay on the current page
            const response = await axios.post(baseUrl, formData, {
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            setLoading(false);
            toast.success('Tool created successfully', {
                description: `Created tool "${formData.name}"`,
            });
            
            // Call success callback with created tool data
            if (onSuccess && response.data) {
                onSuccess(response.data);
            }
            
            onClose();
        } catch (error) {
            setLoading(false);
            
            if (axios.isAxiosError(error) && error.response?.status === 422) {
                // Validation errors
                const validationErrors = error.response.data.errors || {};
                logger.error('Failed to create tool:', { errors: validationErrors });
                setErrors(validationErrors as Record<string, string>);
            } else {
                logger.error('Failed to create tool:', { error });
                toast.error('Failed to create tool', {
                    description: 'An unexpected error occurred. Please try again.',
                });
            }
        }
    };

    const inputClassName = "w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";
    const errorInputClassName = "w-full rounded-md border border-red-300 bg-red-50/50 py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                        {/* Header */}
                        <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50 relative">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <div className={`flex h-10 w-10 items-center justify-center rounded-full ${colors.bgLight}`}>
                                        <Wrench className={`h-5 w-5 ${colors.text}`} />
                                    </div>
                                    <h2 className="text-2xl font-semibold text-slate-900">
                                        Add New Tool
                                    </h2>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onClose();
                                    }}
                                    className="rounded-full p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <p className="text-sm text-slate-500">
                                Register a new tool that agents can use.
                            </p>
                        </div>

                        {/* Form Content */}
                        <div className="flex-1 overflow-y-auto p-8 bg-white">
                            <div className="space-y-6 max-w-2xl mx-auto">
                                <div className="space-y-2">
                                    <Label>Tool Name <span className="text-status-error-text">*</span></Label>
                                    <Input
                                        placeholder="e.g. web-search"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        className={errors.name ? errorInputClassName : inputClassName}
                                        autoFocus
                                    />
                                    {errors.name && (
                                        <p className="text-xs text-red-500">{errors.name}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>Handler Class</Label>
                                    <Input
                                        placeholder="e.g. App\\Tools\\WebSearchTool"
                                        value={handlerClass}
                                        onChange={e => setHandlerClass(e.target.value)}
                                        className={`font-mono text-xs ${inputClassName}`}
                                    />
                                    <p className="text-xs text-slate-400">
                                        The fully qualified class name that implements this tool.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label>Description</Label>
                                    <textarea
                                        placeholder="Brief description of what this tool does..."
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        rows={3}
                                        className={`${inputClassName} resize-none`}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Tags</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Add a tag..."
                                            value={tagInput}
                                            onChange={e => setTagInput(e.target.value)}
                                            onKeyDown={handleTagKeyDown}
                                            className={inputClassName}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddTag}
                                            disabled={!tagInput.trim()}
                                            className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                    {tags.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {tags.map((tag) => (
                                                <span
                                                    key={tag}
                                                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bgLight} ${colors.text}`}
                                                >
                                                    {tag}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveTag(tag)}
                                                        className="hover:opacity-70"
                                                    >
                                                        <XCircle size={14} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <VisibilitySelect
                                    value={visibility}
                                    onChange={setVisibility}
                                    className={inputClassName}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50 flex justify-end items-center gap-3">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClose();
                                }}
                                disabled={loading}
                                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-md transition-all hover:bg-slate-50 hover:shadow-lg disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSubmit();
                                }}
                                disabled={loading || !name.trim()}
                                className="flex items-center gap-2 rounded-lg bg-btn-primary px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:bg-btn-primary-hover hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    'Create Tool'
                                )}
                            </button>
                        </div>
        </SlideDrawer>
    );
}
